import { useMemo } from "react";
import { usePopoverContext } from "@ark-ui/react";
import classnames from "classnames";

import { useTranslation } from "@/lib/i18n";
import { useDashboardStoreActions, useSelectedTimeRange } from "@/features/dashboard/store";
import { useDashboardData } from "@/features/shared/hooks";
import Button from "@/components/button/button";
import { formatTimeOptionLabel } from "@/features/dashboard/components/time-location-selector/format-time-range";

import styles from "./time-range-panel.module.css";

interface TimeRangePanelProps {
  /** Called by the Done button to dismiss the host popover. */
  onDone?: () => void;
}

/**
 * Time-span dropdown body: two columns (From / To) of selectable option rows.
 * Picks are applied live to the shared runtime and the panel stays open; the
 * host surface decides how it is dismissed via {@link TimeRangePanelProps.onDone}.
 */
export default function TimeRangePanel({ onDone }: TimeRangePanelProps) {
  const { t } = useTranslation();
  const { hoursStart, hoursEnd } = useSelectedTimeRange();
  const { selectedDashboard } = useDashboardData();
  const { setTimeRange } = useDashboardStoreActions();

  const [startOptions, endOptions] = useMemo(() => {
    const settings = selectedDashboard?.time_range_selector_settings;
    return [settings?.start_options ?? [], settings?.end_options ?? []];
  }, [selectedDashboard]);

  if (!selectedDashboard) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.columns}>
        <TimeColumn
          title={t("common.from")}
          options={startOptions}
          selected={hoursStart}
          onSelect={(startHour) => setTimeRange({ startHour })}
          formatLabel={(hours) => formatTimeOptionLabel(hours, t)}
        />
        <TimeColumn
          title={t("common.to")}
          options={endOptions}
          selected={hoursEnd}
          onSelect={(endHour) => setTimeRange({ endHour })}
          formatLabel={(hours) => formatTimeOptionLabel(hours, t)}
        />
      </div>
      <Button variant="solid" className={styles.done} onClick={onDone}>
        {t("common.done")}
      </Button>
    </div>
  );
}

/**
 * Popover-hosted variant: wires the Done button to the surrounding Ark popover's
 * dismiss. Shown directly from the toolbar button; stays open on pick (clicks
 * are inside the popover) and closes on Done, outside click, or Escape.
 */
export function PopoverTimeRangePanel() {
  const popover = usePopoverContext();

  return <TimeRangePanel onDone={() => popover.setOpen(false)} />;
}

interface TimeColumnProps {
  title: string;
  options: number[];
  selected: number | undefined;
  onSelect: (hours: number) => void;
  formatLabel: (hours: number) => string;
}

function TimeColumn({ title, options, selected, onSelect, formatLabel }: TimeColumnProps) {
  return (
    <div className={styles.column}>
      <div className={styles.columnTitle}>{title}</div>
      <div className={styles.options} role="listbox" aria-label={title}>
        {options.map((hours) => {
          const isSelected = hours === selected;

          return (
            <button
              key={hours}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={classnames(styles.option, {
                [styles.optionSelected]: isSelected,
              })}
              onClick={() => onSelect(hours)}
            >
              {formatLabel(hours)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
